import { Dribbble, Facebook, Globe, Mail, Youtube } from 'lucide-react';
import type { SocialPlatform } from '@portfolio/shared';
import instagramSvg from '@/assets/icons/social/instagram.svg?raw';
import linkedinSvg from '@/assets/icons/social/linkedin.svg?raw';
import xSvg from '@/assets/icons/social/x.svg?raw';

const svg = (d: string | string[], viewBox = '0 0 24 24') =>
  function Icon({ className }: { className?: string }) {
    return (
      <svg viewBox={viewBox} className={className} fill="currentColor" aria-hidden>
        {[d].flat().map((path) => (
          <path key={path} d={path} />
        ))}
      </svg>
    );
  };

/** Icon from one of the design's SVG files (src/assets/icons/social), drawn in currentColor. */
const fromFile = (markup: string) =>
  svg(
    [...markup.matchAll(/\sd="([^"]+)"/g)].map((m) => m[1]!),
    /viewBox="([^"]+)"/.exec(markup)?.[1],
  );

const XIcon = fromFile(xSvg);
const LinkedinIcon = fromFile(linkedinSvg);
const InstagramIcon = fromFile(instagramSvg);
const BehanceIcon = svg(
  'M8.2 11.3c.9-.4 1.4-1.2 1.4-2.2 0-2-1.5-2.6-3.3-2.6H1v11h5.5c2 0 3.9-1 3.9-3.3 0-1.4-.7-2.5-2.2-2.9ZM3.4 8.4h2.4c.9 0 1.7.3 1.7 1.3s-.7 1.3-1.6 1.3H3.4V8.4Zm2.7 7.3H3.4v-3h2.8c1.1 0 1.8.5 1.8 1.6 0 1.1-.8 1.4-1.9 1.4ZM15 6.9h5.1v1.3H15V6.9Zm2.6 2.7c-2.6 0-4.3 1.9-4.3 4.4 0 2.6 1.6 4.4 4.3 4.4 2 0 3.3-.9 3.9-2.8h-2c-.2.7-1.1 1.1-1.8 1.1-1.3 0-2-.8-2-2.1h6c.2-2.7-1.3-5-4.1-5Zm-1.9 3.6c.1-1.1.8-1.8 1.9-1.8 1.1 0 1.7.7 1.8 1.8h-3.7Z',
);
const TiktokIcon = svg(
  'M16.6 5.82A4.28 4.28 0 0 1 15.54 3h-3.09v12.4a2.59 2.59 0 1 1-2.59-2.59c.27 0 .52.04.76.11V9.75a5.7 5.7 0 0 0-.76-.05 5.69 5.69 0 1 0 5.69 5.7V9.01a7.35 7.35 0 0 0 4.3 1.38V7.3a4.3 4.3 0 0 1-3.25-1.48Z',
);
const WhatsappIcon = svg(
  'M12.04 2a9.9 9.9 0 0 0-8.5 15l-1.4 5 5.13-1.35A9.9 9.9 0 1 0 12.04 2Zm0 18.1a8.2 8.2 0 0 1-4.18-1.15l-.3-.18-3.04.8.81-2.97-.2-.3a8.2 8.2 0 1 1 6.91 3.8Zm4.5-6.14c-.25-.12-1.46-.72-1.69-.8-.23-.09-.39-.13-.55.12-.17.25-.64.8-.78.96-.14.17-.29.19-.53.06a6.7 6.7 0 0 1-3.32-2.9c-.25-.43.25-.4.72-1.33.08-.16.04-.3-.02-.43l-.75-1.8c-.2-.48-.4-.41-.55-.42h-.47a.9.9 0 0 0-.66.31 2.77 2.77 0 0 0-.86 2.06 4.8 4.8 0 0 0 1 2.55 11 11 0 0 0 4.22 3.73c1.57.68 2.19.74 2.98.62.48-.07 1.46-.6 1.67-1.18.2-.58.2-1.07.14-1.18-.06-.1-.22-.16-.47-.29Z',
);

export function SocialIcon({
  platform,
  className = 'size-4',
}: {
  platform: SocialPlatform;
  className?: string;
}) {
  switch (platform) {
    case 'x':
      return <XIcon className={className} />;
    case 'linkedin':
      return <LinkedinIcon className={className} />;
    case 'instagram':
      return <InstagramIcon className={className} />;
    case 'behance':
      return <BehanceIcon className={className} />;
    case 'dribbble':
      return <Dribbble className={className} aria-hidden />;
    case 'facebook':
      return <Facebook className={className} aria-hidden />;
    case 'tiktok':
      return <TiktokIcon className={className} />;
    case 'youtube':
      return <Youtube className={className} aria-hidden />;
    case 'whatsapp':
      return <WhatsappIcon className={className} />;
    case 'email':
      return <Mail className={className} aria-hidden />;
    default:
      return <Globe className={className} aria-hidden />;
  }
}

export const platformName: Record<SocialPlatform, string> = {
  x: 'X (Twitter)',
  linkedin: 'LinkedIn',
  instagram: 'Instagram',
  behance: 'Behance',
  dribbble: 'Dribbble',
  facebook: 'Facebook',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  whatsapp: 'WhatsApp',
  email: 'Email',
  website: 'Website',
};
